import { Button } from '@brightdrop/bd-ui';
import { Box, Typography } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ErrorVariant } from '../../state/chatTypes';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import RefreshIcon from '../icons/RefreshIcon';
import MessageFeedbackRow from '../MessageFeedbackRow';
import useStyles from './AgentErrorCard.styles';

/**
 * `MessageFeedbackRow` is keyed by message id so each row owns its
 * own rating in the chat slice's `submittedFeedback` map. The agent
 * error has no message id of its own, so we namespace the failed
 * user-message id with this prefix to give the error its own
 * feedback slot without colliding with any future assistant reply
 * keyed by the same user message id.
 */
const ERROR_FEEDBACK_KEY_PREFIX = 'agent-error:';

/**
 * Map from `ErrorVariant` to the translation key for the error
 * message body. Centralized here so the API integration step can swap
 * this file's variant -> copy table for a code -> variant -> copy
 * table without touching the consumers.
 */
const VARIANT_MESSAGE_KEYS = {
  temporary: 'chat.error.temporary.title',
  cannotAnswer: 'chat.error.cannotAnswer.title',
  unclassified: 'chat.error.unclassified.title',
  notOnboarded: 'chat.error.notOnboarded.title',
} as const satisfies Record<ErrorVariant, string>;

export type AgentErrorCardProps = {
  /**
   * Stable identifier of the underlying `ErrorEvent`. Passed back
   * through `onRetry` so the consumer can retry the right event when
   * multiple cards live in the timeline.
   */
  errorId: string;
  /** Which inline error variant to render. Drives the body copy. */
  variant: ErrorVariant;
  /**
   * Whether the BE classification (`errorClassification.ts` →
   * `classifySidekickError(code).canRetry`) allows retrying this
   * specific code. When `false`, the Retry button renders in the
   * permanently-disabled grey state per Figma node `15612:63773`
   * from first paint — no rotation, no click handler.
   */
  canRetry: boolean;
  /**
   * Id of the error whose retry is currently in flight, or `null`.
   * Every card is disabled while this is non-null. Only the matching
   * card takes the active treatment:
   *
   *   - the Retry button is disabled (no clicks, no keyboard
   *     activation),
   *   - the refresh-cw-05 glyph spins one revolution per second
   *     clockwise per Figma annotation `15612:63903`,
   *   - `prefers-reduced-motion: reduce` users still get the grey
   *     disabled treatment without the rotating glyph (handled by
   *     the styles file).
   *
   * Set to `null` once a failed retry terminates so the user can try
   * again. A successful retry removes the resolved error event.
   */
  retryingErrorId: string | null;
  /**
   * Whether an assistant response is currently streaming. Retry stays
   * disabled until that turn finishes.
   */
  isStreaming: boolean;
  /**
   * Fired when the user clicks Retry. Receives the `errorId` so the
   * consumer can dispatch against the right `ErrorEvent`. No-op while
   * the button is in any disabled state.
   */
  onRetry: (errorId: string) => void;
  /**
   * Identifier of the failed user message this error responds to.
   * Used to derive the `MessageFeedbackRow` key so the inline thumbs /
   * freeform feedback under the error stores its rating against this
   * specific failure. The namespaced key (`agent-error:${id}`) leaves
   * the raw id free for any future assistant-reply feedback under
   * the same turn. When omitted the feedback row is suppressed (the
   * error still renders).
   */
  sourceUserMessageId?: string;
};

/**
 * Inline error surfaced after the simulated 3-attempt failure flow
 * exhausts its attempts (the `network` variant takes a different
 * path and renders a toast via `useToasts`, not this component).
 *
 * Rendered as plain assistant-message-style body text + a small
 * `button-link` Retry with a refresh icon, matching the Figma
 * `Agent error` frame (`15703:85631`). Intentionally NOT routed
 * through `InlineMessageCard` — that primitive's chrome (white
 * surface, hairline border, 0.75rem padding, 133px filled brand
 * button, h3 title) is the reset-prompt treatment, not the agent-
 * error treatment.
 *
 * Retry button visual contract:
 *
 *   - `canRetry=true` + no active request — enabled, brand blue.
 *   - this card's retry is active — grey + clockwise rotation per Figma node
 *     `15612:63773` / annotation `15612:63903`.
 *   - `canRetry=false` — permanently disabled (grey text + grey icon,
 *     not clickable, not spinning).
 *
 * The card itself never subscribes to the chat slice; the consumer
 * (`MessageList`) reads `selectErrorEvents` + `selectRetryingErrorId`
 * and feeds those values plus the current stream state into each card.
 */
function AgentErrorCard({
  errorId,
  variant,
  canRetry,
  retryingErrorId,
  isStreaming,
  onRetry,
  sourceUserMessageId,
}: AgentErrorCardProps) {
  const { classes, cx } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);

  const isRetryPending = retryingErrorId !== null;
  const isRetrying = retryingErrorId === errorId;
  const isButtonDisabled = !canRetry || isStreaming || isRetryPending;
  const showsActiveRetry = isRetrying && canRetry;

  const handleClick = () => {
    if (isButtonDisabled) {
      return;
    }
    onRetry(errorId);
  };

  return (
    <Box className={classes.root} data-testid="agent-error-card">
      <Box className={classes.responseGroup}>
        <Typography
          variant="small_body_regular"
          component="p"
          className={classes.message}
          data-testid="agent-error-message"
        >
          {t(VARIANT_MESSAGE_KEYS[variant])}
        </Typography>
        <Button
          variant="button-link"
          size="medium"
          width="max-content"
          disabled={isButtonDisabled}
          iconColorRule="stroke"
          onClick={handleClick}
          endIcon={<RefreshIcon size={12} />}
          className={cx(
            classes.retryButton,
            showsActiveRetry && classes.retryButtonRetrying
          )}
          aria-label={
            showsActiveRetry
              ? t('chat.error.retry.loadingAriaLabel')
              : undefined
          }
          aria-busy={showsActiveRetry || undefined}
          data-testid="agent-error-retry"
        >
          {t('chat.error.retry.label')}
        </Button>
      </Box>
      {sourceUserMessageId !== undefined && (
        <MessageFeedbackRow
          messageId={`${ERROR_FEEDBACK_KEY_PREFIX}${sourceUserMessageId}`}
        />
      )}
    </Box>
  );
}

AgentErrorCard.displayName = 'AgentErrorCard';

export default memo(AgentErrorCard);
